import { Splitter, SplitterPanel } from 'primereact/splitter'
import { getItemSessionStorage, getItemWebStorage, setItemSessionStorage } from '../../../features/webStorageSaver'
import { Loader } from '../../../widgets/loaders/Loader'
import VideoFramesWidget from '../../../widgets/panorama/videoFramesWidget/VideoFramesWidget'
import PanoramaWidget from '../../../widgets/panorama/panoramaWidget/PanoramaWidget'
import MapWidget from '../../../widgets/map/mapWidget/MapWidget'
import { getCameraMarkerLayer, getPanoramaVideoLayer, getAllObjectsLayer } from '../../../widgets/map/mapFeatures/layers'
import { useEffect, useState } from 'react'
import { configurePanoramaFilters } from '../../../widgets/map/mapFeatures/helpers'
import { useAppSelector } from '../../../stateManagement/hooks/redux'
import { getFrameOnMapPoint, getFrameOnMapPointAfterDragAndDrop, getPanoramaFiltersForBBOX, positionIsInMapView } from '../../../widgets/map/mapFeatures/mapFunctions/getFrameOnMapPoint/getFrameOnMapPoint'
import { panoramaApiSlice } from '../../../api/rtk/base/slices/panoramaApiSlice'
import { getCameraMarkerStyle } from '../../../widgets/map/mapFeatures/styles/mapStyles'
import { fromLonLat, toLonLat } from 'ol/proj'
import { getModifiedCameraCircle } from '../../../widgets/map/mapFeatures/interactions'
import { unByKey } from 'ol/Observable'
import { setXML } from '../../../stateManagement/reducers/PanoramaSlice'
import { useDispatch } from 'react-redux'
import ContentHeader from '../../../widgets/contentHeader/ContentHeader'

export default function PanoramaPage() {
    const dispatch = useDispatch()
    const currentConfig = getItemWebStorage('currentConfig')
    const { wmsServer, wmsVersion, workspace, panoStart } = currentConfig
    const { auxiliaryLayersDisplayState } = getItemWebStorage("mapSettings") ?? {}
    const { allObjects: isAllObjectsLayerShown } = auxiliaryLayersDisplayState ?? {}
    const splitterInitSizes = getItemSessionStorage('panoramaPageSplitterSizes') ?? [50, 50]
    const { panoramaYearFilter, panoramaSeasonFilter, panoramaDirectionFilter, cameraMarkerLongitude,
        cameraMarkerLatitude, showCameras, panoramaXMLIsLoading, panoramaVideoIsPlaying,
        panoramaWindowIsLoading, panoramaCurrentHLookAngle, panoramaCurrentAzimuth, cameraImageIsRendering } = useAppSelector(state => state.panorama)
    const cameraLayerLongitude = cameraMarkerLongitude ?? panoStart[0]
    const cameraLayerLatitude = cameraMarkerLatitude ?? panoStart[1]
    const isLoading = (!showCameras && panoramaWindowIsLoading && !panoramaVideoIsPlaying) || (showCameras && (panoramaXMLIsLoading || cameraImageIsRendering) && !panoramaVideoIsPlaying)
    const [allObjectsLayer, setAllObjectsLayer] = useState<any>()
    const [panoramaVideoLayer, setPanoramaVideoLayer] = useState<any>()
    const [cameraMarkerLayer] = useState(getCameraMarkerLayer([cameraLayerLongitude, cameraLayerLatitude], getCameraMarkerStyle(panoramaCurrentHLookAngle + panoramaCurrentAzimuth)))
    const [mapInteractions, setMapInteractions] = useState<any>([])
    const [mapEventHandlers, setMapEventHandlers] = useState<any>([])
    const [centerOnObjectCoordinates, setCenterOnObjectCoordinates] = useState<any>();

    const [fetchPanoramaFiltersQuery] = panoramaApiSlice.useLazyFetchPanoramaFiltersQuery();
    const [fetchFrameInPointQuery] = panoramaApiSlice.useLazyFetchFrameInPointQuery();

    useEffect(() => {
        let layerFilter: any = configurePanoramaFilters(panoramaDirectionFilter, panoramaSeasonFilter, panoramaYearFilter)
        const panoramaVideoLayer = getPanoramaVideoLayer(wmsServer, wmsVersion, workspace, layerFilter)
        setPanoramaVideoLayer(panoramaVideoLayer)
    }, [panoramaYearFilter, panoramaSeasonFilter, panoramaDirectionFilter])

    useEffect(() => {
        if (panoramaVideoLayer) {
            getFrameOnMapPoint({ fetchFrameInPointQuery, isAfterLayerChange: true })
        }
    }, [panoramaVideoLayer])

    useEffect(() => {
        if (isAllObjectsLayerShown) {
            const allObjectsLayer = getAllObjectsLayer()
            setAllObjectsLayer(allObjectsLayer)
        }
        return () => {
            dispatch(setXML(null))
        }
    }, [])

    useEffect(() => {
        if (cameraMarkerLongitude && cameraMarkerLatitude) {
            let coordinates = fromLonLat([cameraMarkerLongitude, cameraMarkerLatitude])
            cameraMarkerLayer?.getSource()?.getFeatures()[0]?.getGeometry()?.setCoordinates(coordinates)
            if (!positionIsInMapView(coordinates)) {
                setCenterOnObjectCoordinates(coordinates)
            }
        }
    }, [cameraMarkerLongitude, cameraMarkerLatitude])

    useEffect(() => {
        const rotateAngle = panoramaCurrentHLookAngle + panoramaCurrentAzimuth
        cameraMarkerLayer?.getSource()?.getFeatures()[0]?.setStyle(getCameraMarkerStyle(rotateAngle))
    }, [panoramaCurrentHLookAngle, panoramaCurrentAzimuth])

    function splitterResizeFinished(event: any) {
        let sizes = event?.sizes
        setItemSessionStorage('panoramaPageSplitterSizes', sizes)
    }

    function applyMapFrameFind(map: any) {
        const clickFunc = (event: any) => {
            const [longitude, latitude] = toLonLat(event?.coordinate)
            getFrameOnMapPoint({ fetchFrameInPointQuery, argsLongitude: longitude, argsLatitude: latitude })
        }
        map?.on("singleclick", clickFunc)
        return {
            off: () => {
                map?.un("singleclick", clickFunc)
            }
        }
    }

    function applyMapPanoramaFiltersDefine(map: any) {
        const moveEndFunc = (event: any) => getPanoramaFiltersForBBOX(fetchPanoramaFiltersQuery, map)
        map?.on("moveend", moveEndFunc)
        return {
            off: () => {
                map?.un("moveend", moveEndFunc)
            }
        }
    }

    useEffect(() => {
        const mapEventHandlers = [
            {
                name: "frameFind",
                func: applyMapFrameFind
            },
            {
                name: "panoramaFiltersDefine",
                func: applyMapPanoramaFiltersDefine
            },
        ]
        setMapEventHandlers(mapEventHandlers)
    }, [])

    function applyModifyPanoramaCameraInteraction(map: any) {
        // const modifyCameraView = getModifiedCameraView(cameraMarkerLayer, cameraMarkerLayer.getSource())
        const modifyCameraCircle = getModifiedCameraCircle(cameraMarkerLayer.getSource())
        const cameraCircleModifyEndFunc: any = (event: any) => getFrameOnMapPointAfterDragAndDrop({ fetchFrameInPointQuery, cameraMarkerLayer })
        modifyCameraCircle.on('modifyend', cameraCircleModifyEndFunc);
        map.addInteraction(modifyCameraCircle);
        // map.addInteraction(modifyCameraView);
        return {
            off: () => {
                map.removeInteraction(modifyCameraCircle);
                unByKey(cameraCircleModifyEndFunc)
            }
        }
    }

    useEffect(() => {
        const mapInteractions = [
            {
                name: "modifyPanoramaCamera",
                func: applyModifyPanoramaCameraInteraction
            }
        ]
        setMapInteractions(mapInteractions)
    }, [])

    return (
        <>
            <ContentHeader title="Видеоданные" type="panorama" />
            <div className='panorama-page-div'>
                <Splitter layout="vertical" onResizeEnd={(event) => splitterResizeFinished(event)} gutterSize={10}>
                    <SplitterPanel size={splitterInitSizes[0]} minSize={10}>
                        <div className="panorama-div">
                            {isLoading && <Loader />}
                            {showCameras
                                ?
                                <VideoFramesWidget />
                                :
                                <PanoramaWidget />
                            }
                        </div>
                    </SplitterPanel>
                    <SplitterPanel size={splitterInitSizes[1]} minSize={10}>
                        <div className="panorama-map-div">
                            {isLoading && <Loader />}
                            <MapWidget
                                additionalLayers={[allObjectsLayer, panoramaVideoLayer, cameraMarkerLayer]}
                                mapEventHandlers={mapEventHandlers}
                                mapInteractions={mapInteractions}
                                mapInfoWidgetPositionIsTop
                                centerOnObjectCoordinates={centerOnObjectCoordinates}
                                mapType="panorama"
                            />
                        </div>
                    </SplitterPanel>
                </Splitter>
            </div>
        </>
    )
}
